// src/pages/parts/orderPickup/components/OrderSerialNumbersTable.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import { skipToken } from '@reduxjs/toolkit/query';
import OrderPickupDialog from './OrderPickupDialog';
import OtherInvoiceDialog from './OtherInvoiceDialog';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton';
import { useGetSapInvoicesQuery, useGetExternalInvoicesQuery, useLazyExportSapInvoicesToExcelQuery, useLazyExportExternalInvoicesToExcelQuery } from '../../../../api/endpoints/parts';
import { useGetPreAccountingListQuery } from '../../../../api/endpoints/preAccounting';
import { usePreAccountingCustomerOptions } from '../../../../hooks/usePreAccountingCustomerOptions';
import { useLanguage } from '../../../../contexts/LanguageContext';
import GenericTableFilter from '../../../../components/common/GenericTableFilter';

const OrderSerialNumbersTable = () => {
  const { th, getCurrentLanguageId } = useLanguage();
  const langId = getCurrentLanguageId();
  const [company, setCompany] = useState('karsan');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [receivedTabIndex, setReceivedTabIndex] = useState(0);
  const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1, sortField: null, sortOrder: null });
  const [activeFilters, setActiveFilters] = useState({ invoiceNo: '', eInvoiceNo: '' });
  const [isExporting, setIsExporting] = useState(false);

  const isReceived = company === 'karsan' ? receivedTabIndex === 1 : false;
  const karsanExcelParams = {
    Received: receivedTabIndex === 1,
    ...(activeFilters.invoiceNo ? { InvoiceNo: activeFilters.invoiceNo } : {}),
    ...(activeFilters.eInvoiceNo ? { efatura: activeFilters.eInvoiceNo } : {}),
  };

  useEffect(() => {
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = isExporting ? 'wait' : '';
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [isExporting]);

  // --- Customer dropdown state for "other" mode ---
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [dropdownFilterValue, setDropdownFilterValue] = useState('');
  const dropdownDebounceRef = useRef(null);

  const { customerOptions, isFetching: fetchingCustomers } = usePreAccountingCustomerOptions({
    SearchText: customerSearchText,
    RowCount: 50,
    skip: company !== 'other'
  });

  const currentOptions = useMemo(() => {
    const opts = customerOptions || [];
    if (selectedCustomerId != null && selectedCustomerOption) {
      const exists = opts.some((opt) => Number(opt.value) === Number(selectedCustomerOption.value));
      if (!exists) return [selectedCustomerOption, ...opts];
    }
    return opts;
  }, [customerOptions, selectedCustomerId, selectedCustomerOption]);

  const handleDropdownFilterInputChange = useCallback((val) => {
    setDropdownFilterValue(val);
    if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
    dropdownDebounceRef.current = setTimeout(() => {
      setCustomerSearchText((val ?? '').trim());
    }, 400);
  }, []);

  useEffect(() => {
    return () => { if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current); };
  }, []);

  const dropdownFilterTemplate = useCallback(() => (
    <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
      <InputText
        value={dropdownFilterValue}
        onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
        className="p-dropdown-filter w-full"
        placeholder={th('common.search') || 'Ara'}
        autoFocus
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="p-dropdown-filter-icon pi pi-search" />
    </div>
  ), [dropdownFilterValue, handleDropdownFilterInputChange, th]);

  const handleCustomerChange = useCallback((value) => {
    setDropdownFilterValue('');
    setCustomerSearchText('');
    if (value) {
      const found = (customerOptions || []).find((opt) => Number(opt.value) === Number(value));
      setSelectedCustomerOption(found || { label: `${value}`, value: Number(value) });
      setSelectedCustomerId(value);
    } else {
      setSelectedCustomerId(null);
      setSelectedCustomerOption(null);
    }
  }, [customerOptions]);

  // --- Pre-accounting balance query (only for "other" mode) ---
  const balanceArgs = company === 'other' && selectedCustomerId
    ? { DealerId: selectedCustomerId, PageNumber: 0, PageSize: 0, LanguageId: typeof langId === 'number' ? langId : 2, CustomerType: 0 }
    : skipToken;
  const { data: balanceResp } = useGetPreAccountingListQuery(balanceArgs);
  const totalBalances = balanceResp?.data?.totalBalances ?? [];

  // --- Filter fields ---
  const filterFields = useMemo(
    () => {
      const fields = [{ field: 'invoiceNo', headerName: company === 'karsan' ? th('parts.systemInvoiceNo') : th('table.invoiceNo'), type: 'text' }];
      if (company === 'karsan') {
        fields.push({ field: 'eInvoiceNo', headerName: th('parts.eInvoiceNo'), type: 'text' });
      }
      return fields;
    },
    [th, company]
  );

  // --- Invoice queries ---
  const commonArgs = {
    PageNumber: lazyParams.page,
    PageSize: lazyParams.rows,
    InvoiceNo: activeFilters.invoiceNo || '',
    Received: isReceived,
    ...(company === 'karsan' && activeFilters.eInvoiceNo ? { efatura: activeFilters.eInvoiceNo } : {}),
  };
  const { data: karsanData, isFetching: isFetchingKarsan, refetch: refetchKarsan } = useGetSapInvoicesQuery(commonArgs, {
    skip: company !== 'karsan',
    refetchOnMountOrArgChange: true,
  });
  const { data: otherData, isFetching: isFetchingOther, refetch: refetchOther } = useGetExternalInvoicesQuery(commonArgs, {
    skip: company !== 'other',
    refetchOnMountOrArgChange: true,
  });

  const activeData = company === 'karsan' ? karsanData : otherData;
  const isFetching = company === 'karsan' ? isFetchingKarsan : isFetchingOther;

  const rawItems = activeData?.data?.items ?? [];
  const totalRecords = activeData?.data?.totalCount ?? 0;

  const sortedData = useMemo(() => {
    if (!lazyParams.sortField) return rawItems;
    const order = lazyParams.sortOrder === 1 ? 1 : -1;
    return [...rawItems].sort((a, b) => {
      const aVal = a[lazyParams.sortField] ?? '';
      const bVal = b[lazyParams.sortField] ?? '';
      return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
    });
  }, [rawItems, lazyParams.sortField, lazyParams.sortOrder]);

  const onPage = (event) => {
    const calculatedPage = Math.floor(event.first / event.rows) + 1;
    setLazyParams((prev) => ({ ...prev, first: event.first, rows: event.rows, page: calculatedPage }));
  };

  const onSort = (event) => {
    setLazyParams((prev) => ({ ...prev, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  const handleEdit = (row) => {
    setSerialNumber(row.invoiceNo || '');
    setDialogVisible(true);
  };

  const handleAdd = () => {
    setSerialNumber('');
    setDialogVisible(true);
  };

  useEffect(() => {
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
  }, [company, activeFilters, receivedTabIndex]);

  // Reset customer selection when switching away from "other"
  useEffect(() => {
    if (company !== 'other') {
      setSelectedCustomerId(null);
      setSelectedCustomerOption(null);
      setDropdownFilterValue('');
      setCustomerSearchText('');
    }
  }, [company]);

  const actionBodyTemplate = (row) => (
    <Button icon="pi pi-pencil" rounded text aria-label={th('parts.edit')} onClick={() => handleEdit(row)} />
  );

  const handleAdvancedFilterChange = (updated) => {
    setActiveFilters({ invoiceNo: updated.invoiceNo || '', eInvoiceNo: updated.eInvoiceNo || '' });
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
  };

  const initFilters = () => {
    setActiveFilters({ invoiceNo: '', eInvoiceNo: '' });
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, sortField: null, sortOrder: null }));
  };

  const handleReceivedTabChange = (e) => {
    setReceivedTabIndex(e.index);
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, sortField: null, sortOrder: null }));
  };

  // --- Shared table content ---
  const tableContent = (
    <>
      <div className="mb-2">
        <GenericTableFilter
          filterFields={filterFields}
          initialFilters={{ invoiceNo: '', eInvoiceNo: '' }}
          onApplyFilters={handleAdvancedFilterChange}
          onResetFilters={initFilters}
        />
      </div>

      {company === 'other' && (
        <div className="mb-3">
          <div className="grid align-items-end">
            <div className="col-12 md:col-4">
              <label className="block mb-1 text-sm font-medium">{th('preAccounting.customer')}</label>
              <Dropdown
                value={selectedCustomerId}
                onChange={(e) => handleCustomerChange(e.value)}
                options={currentOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={th('preAccounting.selectCustomer')}
                className="w-full"
                filter
                filterTemplate={dropdownFilterTemplate}
                showClear
                loading={fetchingCustomers}
                emptyMessage={th('common.noRecords') || 'Kayıt bulunamadı'}
              />
            </div>
            {totalBalances.length > 0 && (
              <div className="col-12 md:col-8">
                <div className="flex align-items-center gap-3 flex-wrap">
                  {totalBalances.map((balance, index) => (
                    <Tag
                      key={`${balance.currency}-${index}`}
                      value={`${th('preAccounting.totalBalance')}: ${balance.amount?.toLocaleString()} ${balance.currency}`}
                      rounded
                      className="text-sm px-3 py-2"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DataTable
        value={sortedData}
        lazy
        paginator
        first={lazyParams.first}
        rows={lazyParams.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        className="p-datatable-gridlines"
        showGridlines
        dataKey="invoiceNo"
        loading={isFetching}
        emptyMessage={th('parts.invoiceNotFound')}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: '50rem' }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th('table.paginatorTemplate')}
      >
        <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
        <Column field="invoiceNo" header={company === 'karsan' ? th('parts.systemInvoiceNo') : th('table.invoiceNo')} sortable style={{ width: '20%' }} />
        {company === 'karsan' && (
          <Column field="efatura" header={th('parts.eInvoiceNo')} sortable style={{ width: '15%' }} />
        )}
        <Column
          field="invoiceDate"
          header={th('table.invoiceDate')}
          sortable
          body={(row) => (row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString('tr-TR') : '')}
          style={{ width: '20%' }}
        />
        <Column field="invoiceAmount" header={th('table.totalAmount')} sortable style={{ width: '20%' }} />
        <Column field="partCount" header={th('parts.quantity')} sortable style={{ width: '20%' }} />
        <Column field="remainingPart" header={th('order.remainingQty')} sortable style={{ width: '20%' }} />
        <Column field="dln" header={th('table.deliveryNote')} sortable style={{ width: '20%' }} />
      </DataTable>
    </>
  );

  const excelContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const excelButtonProps = {
    buttonLabel: th('common.export'),
    buttonClassName: 'p-button-success',
    icon: 'pi pi-file-excel',
    disabled: isExporting,
    defaultContentType: excelContentType,
    dataExtractor: (result) => result?.fileBase64,
    fileNameExtractor: (result) => result?.fileName,
    contentTypeExtractor: (result) => result?.contentType || excelContentType,
    onExportStart: () => setIsExporting(true),
    onExportSuccess: () => setIsExporting(false),
    onExportError: () => setIsExporting(false),
  };

  return (
    <div style={isExporting ? { cursor: 'wait' } : undefined}>
      <BlockUI
        blocked={isExporting}
        template={
          <div className="flex flex-column align-items-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span>{th('common.downloading')}</span>
          </div>
        }
      >
      <div className="grid mb-3 align-items-end">
        <div className="col-12 md:col-8">
          <div className="flex align-items-center gap-4">
            <div className="flex align-items-center gap-2" style={{ height: 40 }}>
              <RadioButton inputId="rbKarsan" name="company" value="karsan" checked={company === 'karsan'} onChange={(e) => setCompany(e.value)} />
              <label htmlFor="rbKarsan" className="mb-0">Karsan</label>
              <RadioButton inputId="rbOther" name="company" value="other" checked={company === 'other'} onChange={(e) => setCompany(e.value)} />
              <label htmlFor="rbOther" className="mb-0">{th('preApproval.form.otherOption')}</label>
            </div>
          </div>
        </div>
        <div className="col-12 md:col-4 flex justify-content-end gap-2">
          {company === 'karsan' ? (
            <GenericFileExportButton
              key="sap-invoices-excel"
              useExportQuery={useLazyExportSapInvoicesToExcelQuery}
              exportParams={karsanExcelParams}
              defaultFileName="KarsanFaturalari"
              {...excelButtonProps}
            />
          ) : (
            <GenericFileExportButton
              key="external-invoices-excel"
              useExportQuery={useLazyExportExternalInvoicesToExcelQuery}
              exportParams={{}}
              defaultFileName="DisFaturalar"
              {...excelButtonProps}
            />
          )}
          {company === 'other' && (
            <Button label={th('common.add')} icon="pi pi-plus" severity="success" onClick={handleAdd} />
          )}
        </div>
      </div>

      {company === 'karsan' ? (
        <TabView activeIndex={receivedTabIndex} onTabChange={handleReceivedTabChange}>
          <TabPanel header={th('parts.notReceivedTab')}>
            {tableContent}
          </TabPanel>
          <TabPanel header={th('parts.receivedTab')}>
            {tableContent}
          </TabPanel>
        </TabView>
      ) : (
        tableContent
      )}

      {company === 'karsan' ? (
        <OrderPickupDialog
          key={dialogVisible ? (serialNumber || 'new-karsan') : 'hidden'}
          visible={dialogVisible}
          onHide={() => setDialogVisible(false)}
          invoiceNo={serialNumber}
          onSuccess={() => refetchKarsan()}
        />
      ) : (
        <OtherInvoiceDialog
          key={dialogVisible ? (serialNumber || 'new-other') : 'hidden'}
          visible={dialogVisible}
          onHide={() => setDialogVisible(false)}
          invoiceNo={serialNumber}
          onSuccess={() => refetchOther()}
        />
      )}
      </BlockUI>
    </div>
  );
};

export default OrderSerialNumbersTable;
